import { appClient } from 'api/apiClient';
import { buildPageQuery, type Paginated } from 'api/v1Helpers';

export type MemberInvitationStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';

export interface MemberInvitation {
    id: string;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    status: MemberInvitationStatus;
    expiresAt: string;
    createdAt: string;
    acceptedAt?: string | null;
}

export interface CreateMemberInvitationPayload {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

export interface ProvisionMemberResult {
    kind: 'provisioned' | 'added';
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    membership: {
        id: string;
        parishId: string;
        isDefault: boolean;
        joinedAt: string;
    };
}

export async function createMemberInvitation(parishId: string, payload: CreateMemberInvitationPayload): Promise<ProvisionMemberResult> {
    return appClient.post<ProvisionMemberResult>(`parishes/${parishId}/member-invitations`, payload);
}

export interface AcceptMemberInvitationResult {
    parishId: string;
    membership: {
        id: string;
        isDefault: boolean;
        joinedAt: string;
    };
}

export async function fetchMemberInvitations(
    parishId: string,
    opts?: { page?: number; pageSize?: number; status?: string }
): Promise<Paginated<MemberInvitation>> {
    // Community.tsx's Pending Invitations tab is server-paginated (see parishMemberSlice.ts,
    // which passes real page/pageSize matching the UI's "Rows per page"). The `?? 100` default
    // here only applies if a caller omits opts entirely; 100 is also the backend's own
    // pagination cap (paginationSchema: pageSize.max(100).catch(100)) — requesting more would
    // just be silently clamped. The endpoint has no text-search param, so the tab's search box
    // only ever filters whatever page is currently loaded, not the full roster.
    const params = buildPageQuery(opts?.page ?? 1, opts?.pageSize ?? 100, {
        status: opts?.status,
    });
    return appClient.get<Paginated<MemberInvitation>>(`parishes/${parishId}/member-invitations?${params.toString()}`);
}

export async function revokeMemberInvitation(parishId: string, invitationId: string): Promise<void> {
    await appClient.delete(`parishes/${parishId}/member-invitations/${invitationId}`);
}

export async function acceptMemberInvitation(token: string): Promise<AcceptMemberInvitationResult> {
    return appClient.post<AcceptMemberInvitationResult>('member-invitations/accept', { token });
}

export interface MemberInvitationPreview {
    email: string;
    status: MemberInvitationStatus;
    expiresAt: string;
    parishName: string;
}

/** Public — no JWT required. Used to lock the registration email to the invite. */
export async function previewMemberInvitation(token: string): Promise<MemberInvitationPreview> {
    const params = new URLSearchParams({ token });
    return appClient.get<MemberInvitationPreview>(`member-invitations/preview?${params.toString()}`);
}
